import { useEffect, useState } from "react";
import { useAdmin } from "./AdminApp";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Plus, Trash2 } from "lucide-react";
import { PageTitle } from "./Layout";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Slider } from "../../components/ui/slider";
import { Field, I18nInput, ImageUpload } from "../../components/admin/Fields";
import { useSection, Card, Fold, Row, VisGroup } from "../../components/admin/Section";
import RichEditor from "../../components/admin/RichEditor";
import { TextStyleEditor } from "../../components/admin/TextStyleEditor";
import { IconPicker } from "../../components/admin/IconPicker";
import { HeroButtonsEditor } from "../../components/admin/HeroButtonsEditor";

const SOCIALS = [["x", "X (Twitter)"], ["instagram", "Instagram"], ["facebook", "Facebook"], ["youtube", "YouTube"], ["linkedin", "LinkedIn"], ["tiktok", "TikTok"]];
const PRESETS = ["#DC2626", "#E11D48", "#EA580C", "#D97706", "#16A34A", "#0284C7", "#1D4ED8", "#7C3AED"];

function ListEditor({ label, items, onChange, blank, render, testid }) {
  return (
    <Field label={label}>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={`${i}-${items.length}`} className="flex gap-2 items-start" data-testid={`${testid}-row-${i}`}>
            <div className="flex-1">{render(it, (v) => onChange(items.map((x, j) => (j === i ? v : x))))}</div>
            <Button type="button" size="icon" variant="ghost" onClick={() => onChange(items.filter((_, j) => j !== i))} className={label ? "mt-6" : "mt-1"}><Trash2 size={14} className="text-red-500" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...items, blank])} data-testid={`add-${testid}`}><Plus size={14} className="mr-1" />Ekle</Button>
      </div>
    </Field>
  );
}

function SocialsEditor({ f, set }) {
  const split = f.socials_split || {};
  const setUrl = (key, k) => (e) => set(key)({ ...(f[key] || {}), [k]: e.target.value });
  return (
    <Field label="Sosyal hesaplar">
      <div className="space-y-3">
        {SOCIALS.map(([k, l]) => (
          <Fold key={k} title={l} meta={split[k] ? "Ayrı" : f.socials?.[k] || "Boş"} testid={`social-row-${k}`}>
            <div className="flex items-center justify-end gap-3">
              <div className="inline-flex rounded-md border bg-white p-0.5 text-xs">
                {[[false, "Ortak kullan"], [true, "Ayır"]].map(([v, lbl]) => (
                  <button key={lbl} type="button" onClick={() => set("socials_split")({ ...split, [k]: v })} data-testid={`social-${k}-${v ? "split" : "shared"}`}
                    className={`px-3 py-1 rounded transition-colors ${!!split[k] === v ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}>{lbl}</button>
                ))}
              </div>
            </div>
            {split[k] ? (
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Normal tema"><Input placeholder="https://" value={f.socials?.[k] || ""} onChange={setUrl("socials", k)} data-testid={`profile-social-${k}`} /></Field>
                <Field label="Adaylık teması"><Input placeholder="https://" value={f.socials_campaign?.[k] || ""} onChange={setUrl("socials_campaign", k)} data-testid={`profile-social-campaign-${k}`} /></Field>
              </div>
            ) : (
              <Input placeholder="https://" value={f.socials?.[k] || ""} onChange={setUrl("socials", k)} data-testid={`profile-social-${k}`} />
            )}
          </Fold>
        ))}
        <CustomSocials f={f} set={set} />
      </div>
    </Field>
  );
}

function CustomSocials({ f, set }) {
  const list = f.socials_custom || [];
  const upd = (i, k, v) => set("socials_custom")(list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <>
      {list.map((x, i) => (
        <Fold key={`${i}-${list.length}`} title={x.label || "Yeni hesap"} meta={x.split ? "Ayrı" : x.url || "Boş"} testid={`custom-social-${i}`} defaultOpen={!x.label && !x.url}>
          <div className="grid md:grid-cols-[1fr_170px] gap-3">
            <Field label="Hesap adı"><Input value={x.label || ""} onChange={(e) => upd(i, "label", e.target.value)} placeholder="ör. Telegram" data-testid={`custom-social-label-${i}`} /></Field>
            <Field label="İkon"><IconPicker value={x.icon || "link"} onChange={(v) => upd(i, "icon", v)} testid={`custom-social-icon-${i}`} /></Field>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="inline-flex rounded-md border bg-white p-0.5 text-xs">
              {[[false, "Ortak kullan"], [true, "Ayır"]].map(([v, lbl]) => (
                <button key={lbl} type="button" onClick={() => upd(i, "split", v)} data-testid={`custom-social-${i}-${v ? "split" : "shared"}`}
                  className={`px-3 py-1 rounded transition-colors ${!!x.split === v ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}>{lbl}</button>
              ))}
            </div>
            <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => set("socials_custom")(list.filter((_, j) => j !== i))} data-testid={`custom-social-delete-${i}`}><Trash2 size={14} className="mr-1" />Sil</Button>
          </div>
          <div className={`grid gap-3 ${x.split ? "md:grid-cols-2" : ""}`}>
            <Field label={x.split ? "Normal tema" : "Bağlantı"}><Input placeholder="https://" value={x.url || ""} onChange={(e) => upd(i, "url", e.target.value)} data-testid={`custom-social-url-${i}`} /></Field>
            {x.split && <Field label="Adaylık teması"><Input placeholder="https://" value={x.url_campaign || ""} onChange={(e) => upd(i, "url_campaign", e.target.value)} data-testid={`custom-social-url-campaign-${i}`} /></Field>}
          </div>
        </Fold>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => set("socials_custom")([...list, { label: "", icon: "link", url: "" }])} data-testid="add-custom-social"><Plus size={14} className="mr-1" />Yeni hesap ekle</Button>
    </>
  );
}

function ProfileCommon() {
  const { f, set, save, busy } = useSection("profile");
  return (
    <Card title="Ortak bilgiler" sub="İki temada da görünen bilgiler. “Ben Kimim” içeriği Sayfalar bölümünden yönetilir." onSave={save} busy={busy} testid="profile-card">
      <Field label="Ad Soyad"><Input value={f.name || ""} onChange={set("name")} data-testid="profile-name" /></Field>
      <SocialsEditor f={f} set={set} />
    </Card>
  );
}

function ProfileNormal() {
  const { f, set, save, busy, setSave } = useSection("profile");
  return (
    <Card title="1. Karşılama alanı (banner)" sub="Anasayfanın en üstünde görünen fotoğraf, rozet, kısa tanım ve slogan." onSave={save} busy={busy} testid="profile-normal-card">
      <Row f={f} set={set} k="photo"><ImageUpload label="Giriş fotoğrafı (sağda görünür)" value={f.blog_photo} onChange={setSave("blog_photo")} testid="profile-blog-photo" alt={f.name} /></Row>
      <label className="flex items-center gap-3 text-sm text-stone-600 -mt-2"><Switch checked={f.hero_photo_motion !== false} onCheckedChange={(v) => set("hero_photo_motion")(v)} data-testid="hero-photo-motion" />Fotoğrafa hafif hareket efekti (süzülme)</label>
      {f.hero_photo_motion !== false && (
        <div className="flex items-center gap-3 text-sm text-stone-600 -mt-1 pl-11" data-testid="hero-photo-motion-intensity-wrap">
          <span className="shrink-0">Yoğunluk</span>
          <input type="range" min="10" max="200" step="5" value={f.hero_photo_motion_intensity ?? 100}
            onChange={(e) => set("hero_photo_motion_intensity")(Number(e.target.value))}
            className="flex-1 accent-stone-700" data-testid="hero-photo-motion-intensity" aria-label="Hareket yoğunluğu" />
          <span className="tabular-nums text-xs text-stone-500 w-10 text-right">%{f.hero_photo_motion_intensity ?? 100}</span>
        </div>
      )}
      <Row f={f} set={set} k="tagline"><I18nInput label="Kısa tanım (ör. Girişimci • Yazar)" value={f.tagline} onChange={set("tagline")} testid="profile-tagline" maxLength={80} /></Row>
      <Row f={f} set={set} k="badge"><I18nInput label="Rozet yazısı" value={f.badge} onChange={set("badge")} testid="profile-badge" maxLength={60} /></Row>
      <Row f={f} set={set} k="headline"><I18nInput label="Ana slogan (son 2 kelime vurgulu)" value={f.headline} onChange={set("headline")} testid="profile-headline" maxLength={140} /></Row>
      <TextStyleEditor title="Slogan görünümü (yazı tipi ve renk)" value={f.headline_style} onChange={set("headline_style")} sample={f.headline?.tr} tail={2} parts={["Slogan başı", "Son 2 kelime"]} testid="headline-style" />
      <Row f={f} set={set} k="intro"><I18nInput label="Kısa tanıtım cümlesi" value={f.intro} onChange={set("intro")} multiline rows={2} testid="profile-intro" maxLength={400} /></Row>
      <Row f={f} set={set} k="buttons"><HeroButtonsEditor value={f.hero_buttons} onChange={set("hero_buttons")} /></Row>

    </Card>
  );
}

function ProfilePillars() {
  const { f, set, save, busy } = useSection("profile");
  return (
    <Card title='2. "Ne Yaptım? Ne Yaparım? Ne Yapacağım?" bölümü' sub="Anasayfada Çalışmalarım’ın tümünü küçük kutular halinde gösterir. Buradan bu bölümün başlıklarını yönetirsiniz." onSave={save} busy={busy} testid="profile-pillars-card">
      <Fold title="Bölüm başlıkları ve başlık altı yazı" meta="Üst başlık · Ana başlık · Yazı" testid="disc-head-fold">
        <Row f={f} set={set} k="disc_eyebrow"><I18nInput label="Üst başlık (ör. Çok Yönlü Portföy)" value={f.disc_eyebrow} onChange={set("disc_eyebrow")} testid="disc-eyebrow" maxLength={80} /></Row>
        <TextStyleEditor title="Üst başlık görünümü (yazı tipi ve renk)" value={f.disc_eyebrow_style} onChange={set("disc_eyebrow_style")} sample={f.disc_eyebrow?.tr || "Çok Yönlü Portföy"} testid="disc-eyebrow-style" />
        <Row f={f} set={set} k="disc_title"><I18nInput label="Ana başlık" value={f.disc_title} onChange={set("disc_title")} testid="disc-title" maxLength={120} /></Row>
        <TextStyleEditor title="Ana başlık görünümü (yazı tipi ve renk)" value={f.disc_title_style} onChange={set("disc_title_style")} sample={f.disc_title?.tr || "Ne Yaptım? Ne Yaparım? Ne Yapacağım?"} testid="disc-title-style" />
        <Row f={f} set={set} k="disc_intro"><RichEditor label="Başlık altı yazı" value={f.disc_intro} onChange={set("disc_intro")} testid="disc-intro-editor" minH="min-h-[160px]" /></Row>
      </Fold>
      <VisGroup title="Görünürlük" f={f} set={set} items={[["disciplines", "Bu bölümü anasayfada göster"]]} />
      <p className="text-xs text-stone-500">Bu bölümde listelenen çalışmaları yönetmek için <strong>Çalışmalarım</strong> menüsünü kullanın.</p>
    </Card>
  );
}

function SliderSettings({ value, onChange }) {
  const v = { per_view: 3, autoplay: false, interval: 5, count: 12, ...(value || {}) };
  const set = (k) => (x) => onChange({ ...v, [k]: x });
  const pill = (on) => `px-3 py-1.5 rounded text-xs transition-colors ${on ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`;
  return (
    <Fold title="Anasayfa video slider ayarları" meta={`${v.per_view} video · ${v.autoplay ? `otomatik ${v.interval} sn` : "elle geçiş"}`} testid="slider-settings">
      <Field label="Aynı anda görünen video sayısı">
        <div className="inline-flex rounded-md border bg-white p-0.5" data-testid="slider-per-view">
          {[1, 2, 3, 4].map((n) => <button key={n} type="button" className={pill(v.per_view === n)} onClick={() => set("per_view")(n)} data-testid={`slider-per-view-${n}`}>{n}</button>)}
        </div>
      </Field>
      <Field label="Slider'da toplam kaç video olsun? (en yeniler)">
        <div className="inline-flex flex-wrap rounded-md border bg-white p-0.5" data-testid="slider-count">
          {[3, 6, 9, 12, 18, 24].map((n) => <button key={n} type="button" className={pill(v.count === n)} onClick={() => set("count")(n)} data-testid={`slider-count-${n}`}>{n}</button>)}
        </div>
      </Field>
      <label className="flex items-center gap-3 text-sm"><Switch checked={!!v.autoplay} onCheckedChange={set("autoplay")} data-testid="slider-autoplay" />Otomatik geçiş</label>
      {v.autoplay && (
        <Field label={`Geçiş süresi: ${v.interval} saniye`} hint="Fare slider üzerindeyken otomatik geçiş durur.">
          <Slider min={2} max={15} step={1} value={[v.interval]} onValueChange={([x]) => set("interval")(x)} data-testid="slider-interval" />
        </Field>
      )}
    </Fold>
  );
}

function ProfileSections() {
  const { f, set, save, busy } = useSection("profile");
  return (
    <Card title="3. Alt bölümler ve başlıklar" sub="Alt bölümleri açıp kapatın, bölüm başlıklarının görünümünü ayarlayın (başlık görünümü iki temada da geçerlidir)." onSave={save} busy={busy} testid="profile-sections-card">
      <VisGroup title="Görünürlük" f={f} set={set} items={[["media", "Video galerisi"], ["writings", "Son yazılar"], ["contact", "İletişim"]]} />
      <SliderSettings value={f.video_slider} onChange={set("video_slider")} />
      <TextStyleEditor title="Bölüm başlıkları görünümü (yazı tipi ve renk)" value={f.section_style} onChange={set("section_style")} sample="Disiplinler ve Uzmanlıklar" parts={["Başlık başı", "Son kelime"]} testid="section-style" />
    </Card>
  );
}

function ProfileCampaign() {
  const { f, set, save, busy, setSave } = useSection("profile");
  return (
    <Card title="Adaylık teması profil bilgileri" sub="Yalnızca Adaylık temasında görünür." onSave={save} busy={busy} testid="profile-campaign-card">
      <I18nInput label="Unvan (ör. Milletvekili Aday Adayı)" value={f.title} onChange={set("title")} testid="profile-title" maxLength={80} />
      <I18nInput label="Kısa tanıtım cümlesi (adaylık)" value={f.intro_campaign} onChange={set("intro_campaign")} multiline rows={2} testid="profile-intro-campaign" maxLength={400} />
      <div className="grid md:grid-cols-2 gap-5">
        <ImageUpload label="Giriş fotoğrafı" value={f.photo} onChange={setSave("photo")} testid="profile-photo" alt={f.name} />
        <ImageUpload label="İmza görseli (isteğe bağlı, PNG)" value={f.signature_image} onChange={setSave("signature_image")} testid="profile-signature" alt={`${f.name} imza`} />
      </div>
    </Card>
  );
}

function Theme() {
  const { f, set, save, busy } = useSection("theme");
  return (
    <Card title="Tema" sub="Anahtar değiştiğinde tüm ziyaretçilere anında uygulanır." onSave={save} busy={busy} testid="theme-card">
      <div className="grid sm:grid-cols-2 gap-3">
        {[["normal", "Normal", "Sinematik, koyu ve altın tonlu kişisel site"], ["adaylik", "Adaylık", "Güçlü renkler, kampanya blokları"]].map(([v, l, d]) => (
          <button key={v} type="button" onClick={() => set("mode")(v)} data-testid={`theme-option-${v}`}
            className={`text-left rounded-xl border-2 p-4 transition-colors ${f.mode === v ? "border-stone-900" : "border-stone-200 hover:border-stone-400"}`}>
            <div className={`h-14 rounded-lg mb-3 ${v === "normal" ? "bg-[#120C09] border-b-4 border-[#C9A27E]" : "bg-[#0F172A]"}`} style={v === "adaylik" ? { boxShadow: `inset 0 -8px 0 ${f.accent}` } : {}} />
            <p className="font-semibold">{l}</p><p className="text-xs text-stone-500">{d}</p>
          </button>
        ))}
      </div>
      <Field label="Kampanya vurgu rengi">
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((c) => <button key={c} type="button" onClick={() => set("accent")(c)} style={{ background: c }} className={`w-8 h-8 rounded-full ${f.accent === c ? "ring-2 ring-offset-2 ring-stone-900" : ""}`} aria-label={c} data-testid={`accent-preset-${c}`} />)}
          <input type="color" value={f.accent || "#DC2626"} onChange={(e) => set("accent")(e.target.value.toUpperCase())} className="w-10 h-8 cursor-pointer" data-testid="admin-accent-color-picker" />
          <code className="text-xs">{f.accent}</code>
        </div>
      </Field>
    </Card>
  );
}

function Campaign() {
  const { f, set, save, busy } = useSection("campaign");
  const pr = f.priorities || [];
  const setPr = (i, k, v) => set("priorities")(pr.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  return (
    <Card title="Kampanya içerikleri" sub="Adaylık temasında anasayfada görünür." onSave={save} busy={busy} testid="campaign-card">
      <I18nInput label="Slogan" value={f.slogan} onChange={set("slogan")} testid="campaign-slogan" maxLength={90} />
      <TextStyleEditor title="Slogan görünümü (yazı tipi ve renk)" value={f.slogan_style} onChange={set("slogan_style")} sample={f.slogan?.tr} parts={["Slogan başı", "Son kelime"]} testid="campaign-slogan-style" />
      <I18nInput label="Kayan bant metni" hint="Öğeleri • ile ayırın" value={f.ticker} onChange={set("ticker")} testid="campaign-ticker" maxLength={400} />
      <I18nInput label="Neden Aday Oldum" value={f.why} onChange={set("why")} multiline rows={5} testid="campaign-why" maxLength={3000} />
      <Field label="Öncelikler">
        <div className="space-y-3">
          {pr.map((p, i) => (
            <Fold key={`${i}-${pr.length}`} title={p.title?.tr || `Öncelik ${i + 1}`} meta="Düzenlemek için tıklayın" testid={`priority-row-${i}`}>
              <I18nInput label="Başlık" value={p.title} onChange={(v) => setPr(i, "title", v)} testid={`priority-title-${i}`} />
              <I18nInput label="Açıklama" value={p.text} onChange={(v) => setPr(i, "text", v)} multiline testid={`priority-text-${i}`} />
              <Button type="button" size="sm" variant="ghost" onClick={() => set("priorities")(pr.filter((_, j) => j !== i))} className="text-red-500" data-testid={`priority-delete-${i}`}><Trash2 size={14} className="mr-1" />Sil</Button>
            </Fold>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => set("priorities")([...pr, { title: { tr: "" }, text: { tr: "" } }])} data-testid="add-priority"><Plus size={14} className="mr-1" />Öncelik ekle</Button>
        </div>
      </Field>
      <div className="grid md:grid-cols-2 gap-5 items-end">
        <Field label="Seçim tarihi (geri sayım)"><Input type="datetime-local" value={f.election_date || ""} onChange={set("election_date")} data-testid="campaign-election-date" /></Field>
        <label className="flex items-center gap-3 text-sm pb-2"><Switch checked={!!f.volunteer_enabled} onCheckedChange={set("volunteer_enabled")} data-testid="campaign-volunteer-switch" />"Gönüllü Ol" formu açık</label>
      </div>
    </Card>
  );
}

export default function ProfileAdmin() {
  const { settings } = useAdmin();
  const mode = settings.theme?.mode === "adaylik" ? "adaylik" : "normal";
  const [tab, setTab] = useState(mode);
  useEffect(() => setTab(mode), [mode]);
  return (
    <>
      <PageTitle title="Profil ve tema" sub="Üstteki tema anahtarı hangi temadaysa onun sekmesi açılır. Ortak bilgiler iki temada da kullanılır." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="normal" data-testid="profile-tab-normal">Normal tema{mode === "normal" ? " • yayında" : ""}</TabsTrigger>
          <TabsTrigger value="adaylik" data-testid="profile-tab-adaylik">Adaylık teması{mode === "adaylik" ? " • yayında" : ""}</TabsTrigger>
          <TabsTrigger value="common" data-testid="profile-tab-common">Ortak bilgiler</TabsTrigger>
        </TabsList>
        <TabsContent value="normal"><ProfileNormal /><ProfilePillars /><ProfileSections /></TabsContent>
        <TabsContent value="adaylik"><ProfileCampaign /><Theme /><Campaign /></TabsContent>
        <TabsContent value="common"><ProfileCommon /></TabsContent>
      </Tabs>
    </>
  );
}
